import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, ArrowLeft, MapPin, Calendar as CalendarIcon, Loader2 } from "lucide-react";

type Step = 'address' | 'pickup';

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const ExpressPickupOnboarding = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState<Step>('address');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [createdBoxIds, setCreatedBoxIds] = useState<string[]>([]);
  
  const boxSlots = parseInt(searchParams.get("box_slots") || "0");
  
  // Address fields
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  
  // Pickup fields
  const [pickupDate, setPickupDate] = useState<Date>();
  const [timeSlot, setTimeSlot] = useState<string>("");
  const [instructions, setInstructions] = useState("");

  useEffect(() => {
    initializeOnboarding();
  }, []);

  const initializeOnboarding = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to continue",
          variant: "destructive"
        });
        navigate("/auth");
        return;
      }
      setUser(user);

      // Fetch existing profile data
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profile) {
        setFullName(profile.full_name || "");
        setPhone(profile.phone === '0000000000' ? "" : profile.phone || "");
        setAddressLine1(profile.address_line1 || "");
        setAddressLine2(profile.address_line2 || "");
        setCity(profile.city || "");
        setState(profile.state || "");
        setZip(profile.zip || "");

        // Resume logic
        const stepParam = searchParams.get("step");
        if (stepParam === 'pickup' && profile.onboarding_status === 'address_confirmed') {
          setCurrentStep('pickup');
        } else if (profile.onboarding_status === 'address_confirmed') {
          setCurrentStep('pickup');
        }
      }
    } catch (error) {
      console.error('Error initializing:', error);
    }
  };

  const handleAddressSubmit = async () => {
    // Validation
    if (!fullName.trim() || !phone.trim() || !addressLine1.trim() || !city.trim() || !state || !zip.trim()) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    // Validate name format (letters, spaces, hyphens, apostrophes, periods only)
    if (!/^[a-zA-Z\s\-'.]+$/.test(fullName.trim())) {
      toast({
        title: "Invalid name",
        description: "Name can only contain letters, spaces, hyphens, apostrophes, and periods",
        variant: "destructive"
      });
      return;
    }

    // Clean phone number (remove all non-digits)
    const cleanedPhone = phone.replace(/\D/g, '');
    
    if (cleanedPhone.length !== 10) {
      toast({
        title: "Invalid phone number",
        description: "Phone number must be exactly 10 digits",
        variant: "destructive"
      });
      return;
    }

    // Validate state format (must be 2 uppercase letters)
    if (!/^[A-Z]{2}$/.test(state)) {
      toast({
        title: "Invalid state",
        description: "State must be a 2-letter code (e.g., NY, CA)",
        variant: "destructive"
      });
      return;
    }

    // Validate ZIP code format
    if (!/^\d{5}$/.test(zip)) {
      toast({
        title: "Invalid ZIP code",
        description: "ZIP code must be 5 digits",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      // Update profile with address (use cleaned phone number)
      await supabase
        .from('profiles')
        .update({
          full_name: fullName.trim(),
          phone: cleanedPhone,
          address_line1: addressLine1.trim(),
          address_line2: addressLine2.trim() || null,
          city: city.trim(),
          state: state,
          zip: zip.trim(),
          onboarding_status: 'address_confirmed'
        })
        .eq('id', user.id);

      // Auto-create generic boxes
      const boxesToCreate = Array.from({ length: boxSlots }, (_, index) => ({
        user_id: user.id,
        name: `Box ${index + 1}`,
        category: 'Other',
        location_status: 'at_home',
        source: 'direct_storage_purchase',
        box_number: `BOX-${Date.now()}-${index + 1}`
      }));

      const { data: boxes, error: boxError } = await supabase
        .from('boxes')
        .insert(boxesToCreate)
        .select();

      if (boxError) throw boxError;

      const boxIds = boxes.map(b => b.id);
      setCreatedBoxIds(boxIds);

      // Update slots used
      await supabase
        .from('profiles')
        .update({ box_slots_used: boxSlots })
        .eq('id', user.id);

      toast({ title: "Address confirmed!", description: `${boxSlots} boxes created` });
      setCurrentStep('pickup');
    } catch (error) {
      console.error('Error saving address:', error);
      toast({
        title: "Error",
        description: "Failed to save address",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSchedulePickup = async () => {
    if (!pickupDate || !timeSlot) {
      toast({
        title: "Missing information",
        description: "Please select date and time",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      // Get box IDs if not already created (resuming from saved state)
      let boxIds = createdBoxIds;
      if (boxIds.length === 0) {
        const { data: boxes } = await supabase
          .from('boxes')
          .select('id')
          .eq('user_id', user.id)
          .eq('source', 'direct_storage_purchase')
          .eq('location_status', 'at_home');
        
        if (boxes) {
          boxIds = boxes.map(b => b.id);
          setCreatedBoxIds(boxIds);
        }
      }

      // Map time slot to canonical code
      const timeSlotMap: Record<string, string> = {
        'Morning (9 AM - 12 PM)': 'morning_9-12',
        'Afternoon (12 PM - 3 PM)': 'afternoon_12-3',
        'Evening (3 PM - 6 PM)': 'evening_3-6',
      };

      // Create shipment
      await supabase
        .from('shipments')
        .insert({
          user_id: user.id,
          box_ids: boxIds,
          box_count: boxIds.length,
          type: 'to_storage',
          pickup_date: pickupDate.toISOString().split('T')[0],
          pickup_time_slot: timeSlotMap[timeSlot] || '9-11am',
          status: 'scheduled',
          is_first_pickup: true,
          onboarding_shipment: true
        });

      // Boxes stay as 'home' until physically picked up

      // Set billing start date (pickup date + 2 days)
      const billingStartDate = new Date(pickupDate);
      billingStartDate.setDate(billingStartDate.getDate() + 2);

      // Mark onboarding complete
      await supabase
        .from('profiles')
        .update({
          onboarding_status: 'complete',
          billing_start_date: billingStartDate.toISOString().split('T')[0]
        })
        .eq('id', user.id);

      // Mark payment session complete
      const sessionId = searchParams.get("session_id");
      if (sessionId) {
        await supabase
          .from('payment_sessions')
          .update({ 
            onboarding_completed: true,
            completed_at: new Date().toISOString()
          })
          .eq('stripe_session_id', sessionId);
      }

      toast({ title: "Success!", description: "Pickup scheduled" });
      navigate(`/checkout/success?onboarding=complete&session_id=${sessionId}`);
    } catch (error) {
      console.error('Error scheduling pickup:', error);
      toast({
        title: "Error",
        description: "Failed to schedule pickup",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const progressValue = currentStep === 'address' ? 50 : 100;

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <header className="bg-card border-b">
        <div className="container mx-auto px-4 py-4">
          <span className="text-xl font-bold">Express Pickup Setup</span>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="mb-8">
          <Progress value={progressValue} className="mb-2" />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span className={currentStep === 'address' ? 'text-primary font-semibold' : ''}>
              1. Confirm Address
            </span>
            <span className={currentStep === 'pickup' ? 'text-primary font-semibold' : ''}>
              2. Schedule Pickup
            </span>
          </div>
        </div>

        {currentStep === 'address' && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                <CardTitle>Where should we pick up your boxes?</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Smith"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number (10 digits) *</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                  placeholder="(555) 555-5555"
                  maxLength={14}
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">Required for shipping labels and pickup driver contact</p>
              </div>

              <div>
                <Label htmlFor="address1">Street Address *</Label>
                <Input
                  id="address1"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  placeholder="123 Main St"
                />
              </div>

              <div>
                <Label htmlFor="address2">Apt, Suite, Floor (Optional)</Label>
                <Input
                  id="address2"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  placeholder="Apt 4B"
                />
              </div>

              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-3">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="New York"
                  />
                </div>
                <div className="col-span-1">
                  <Label htmlFor="state">State *</Label>
                  <Select value={state} onValueChange={(value) => setState(value.toUpperCase())}>
                    <SelectTrigger id="state">
                      <SelectValue placeholder="NY" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="zip">ZIP Code *</Label>
                  <Input
                    id="zip"
                    value={zip}
                    onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="10001"
                    maxLength={5}
                  />
                </div>
              </div>

              <Button 
                onClick={handleAddressSubmit} 
                disabled={loading}
                className="w-full"
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {currentStep === 'pickup' && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-primary" />
                <CardTitle>When should we pick up your boxes?</CardTitle>
              </div>
              {addressLine1 && (
                <p className="text-sm text-muted-foreground mt-2">
                  Pickup from: {addressLine1}, {city}, {state} {zip}
                  <Button
                    variant="link"
                    size="sm"
                    className="ml-2 h-auto p-0"
                    onClick={() => setCurrentStep('address')}
                  >
                    Edit
                  </Button>
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="mb-2 block">Pickup Date</Label>
                <Calendar
                  mode="single"
                  selected={pickupDate}
                  onSelect={setPickupDate}
                  disabled={(date) => {
                    const threeDaysFromNow = new Date();
                    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
                    threeDaysFromNow.setHours(0, 0, 0, 0);
                    return date < threeDaysFromNow || 
                      date.getDay() === 0 ||
                      date > new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
                  }}
                  className="rounded-md border"
                />
              </div>

              <div>
                <Label className="mb-2 block">Time Window</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {['Morning (9 AM - 12 PM)', 'Afternoon (12 PM - 3 PM)', 'Evening (3 PM - 6 PM)'].map((slot) => (
                    <Button
                      key={slot}
                      variant={timeSlot === slot ? "default" : "outline"}
                      onClick={() => setTimeSlot(slot)}
                    >
                      {slot}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="instructions">Special Instructions (Optional)</Label>
                <Textarea
                  id="instructions"
                  placeholder="e.g., Ring doorbell, boxes in lobby..."
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setCurrentStep('address')}
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button 
                  onClick={handleSchedulePickup}
                  disabled={loading || !pickupDate || !timeSlot}
                  className="flex-1"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Confirm Pickup
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ExpressPickupOnboarding;
